import React, { useRef, useState, useEffect } from "react";
import { Button, Grid, Paper, Typography, TextField, Snackbar } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import logo from "images/logoAbbott.png";
import routes from "routes";

import { useUserDispatch, INCORRECT_DATA, NO_PROGRAMS_TO_SHOW, USER_DISABLED, USER_DELETED, PHARMACY_DISABLED } from "context/UserContext";
import { encodeRoleName } from "utils/roleNameGetter";
import { LoginWithPin } from "utils/DBFetchers";

function LoginVerification() {
  const { token } = useParams();
  const history = useHistory();
  const dispatch = useUserDispatch();

  const [pin, setPin] = useState(["", "", "", ""]);
  const [userData, setUserData] = useState({ username: "", email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({ message: "", state: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [pendingFocusIndex, setPendingFocusIndex] = useState(null);

  const pinInputRefs = useRef([]);
  const acceptButtonRef = useRef(null);
  
  const isPinComplete = pin.every((digit) => digit);

  useEffect(() => {
    if (pendingFocusIndex === null) return;

    if (pendingFocusIndex >= pin.length) {
      acceptButtonRef.current?.focus();
    } else {
      pinInputRefs.current[pendingFocusIndex]?.focus();
    }

    setPendingFocusIndex(null);
  }, [pendingFocusIndex, pin.length]);

  useEffect(() => {
    if (token) {
      try {
        const payloadBase64 = token.split(".")[1];
        const decodedPayload = JSON.parse(atob(payloadBase64));
        setUserData({
          username: decodedPayload.username || "",
          email: decodedPayload.email || "",
        });
      } catch {
        setError({ message: "Error al decodificar el token", state: true });
      }
    }
  }, [token]);

  const censorEmail = (email) => {
    if (!email || !email.includes("@")) return "";
    const [name, domain] = email.split("@");
    if (name.length <= 3) return `${name[0]}***@${domain}`;
    
    const firstChar = name[0];
    const secondChar = name[1];
    const lastChar = name[name.length - 1];
    return `${firstChar}${secondChar}***${lastChar}@${domain}`;
  };

  const updatePinDigit = (index, event) => {
    const insertedDigit = event.nativeEvent?.data?.replace(/\D/g, "").slice(-1);
    const digit = insertedDigit || event.target.value.replace(/\D/g, "").slice(-1);
    const nextPin = [...pin];
    nextPin[index] = digit;
    if (!digit) {
      nextPin.fill("", index + 1);
    }
    setPin(nextPin);

    if (!digit) return;

    if (index < pin.length - 1) {
      setPendingFocusIndex(index + 1);
      return;
    }

    setPendingFocusIndex(pin.length);
  };

  const pastePin = (index, event) => {
    const pastedDigits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, pin.length - index);

    if (!pastedDigits) return;

    event.preventDefault();

    const nextPin = [...pin];
    for (let digitIndex = 0; digitIndex < pastedDigits.length; digitIndex += 1) {
      nextPin[index + digitIndex] = pastedDigits[digitIndex];
    }
    nextPin.fill("", index + pastedDigits.length);
    setPin(nextPin);
    setPendingFocusIndex(index + pastedDigits.length);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleContinue = () => {
    setIsLoading(true);
    const fullPin = pin.join("");
    const payload = {
      verification_token: token,
      pin: fullPin
    };
    
    LoginWithPin(payload)
      .then((response) => {
        // Checks for programs
        if (response.programs.length === 0) {
          setError({ message: NO_PROGRAMS_TO_SHOW, state: true });
          setIsLoading(false);
          return;
        }
        // Check if user is not deactivated
        if (!response.is_enabled) {
          setError({ message: USER_DISABLED, state: true });
          setIsLoading(false);
          return;
        }
        // Check if user is deleted
        if (response.is_deleted) {
          setError({ message: USER_DELETED, state: true });
          setIsLoading(false);
          return;
        }
        if (response.pharmacy && Object.keys(response.pharmacy).length !== 0 && !response.pharmacy.is_enabled) {
          setError({ message: PHARMACY_DISABLED, state: true });
          setIsLoading(false);
          return;
        }

        localStorage.setItem(
          "switchDisabledActivePrograms",
          response.programs.length === 1
            ? JSON.stringify(response.programs[0])
            : "0",
        );
        localStorage.setItem("token", response.token);

        if (response.programs.length === 1) {
          localStorage.setItem("programId", response.programs[0].toString());
        } else if (response.programs.length > 1) {
          if (response.role.name !== "pharmacies")
            localStorage.setItem(
              "programId",
              response.programs[0].toString(),
            );
          else localStorage.setItem("programId", "1");
        }

        localStorage.setItem("permissions", JSON.stringify(response.permissions));
        localStorage.setItem("username", response.username);
        localStorage.setItem("email", response.email);
        localStorage.setItem("roleId", response.role.id);
        localStorage.setItem("roleName", encodeRoleName(response.role.name));
        localStorage.setItem("authoritarianPrograms", JSON.stringify(response.programs_authoritarian));
        
        setError({ message: INCORRECT_DATA, state: null });
        setIsLoading(false);
        
        dispatch({
          type: "LOGIN_SUCCESS",
          permissions: response.permissions,
        });
        const isExclusiveSoloMujerMedic =
          response.role.name === "medic" &&
          response.programs.length === 1 &&
          Number(response.programs[0]) === 2;

        if (response.role.name === "medic") {
          history.push(
            isExclusiveSoloMujerMedic ? routes.home : routes.sendCoupon,
          );
        } else if (response.role.name === "consultant") {
          history.push(routes.accreditCoupon);
        } else {
          history.push(routes.home);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        const errorData = err?.errors || err;
        setPin(["", "", "", ""]);
        pinInputRefs.current[0]?.focus();
        
        setSnackbar({
          open: true,
          message: errorData?.msg_es || "El PIN ingresado es incorrecto o ha expirado.",
        });
      });
  };

  return (
    <Grid
      container
      style={{ minHeight: "100vh", flexDirection: "column", flexWrap: "nowrap", backgroundColor: "#F6F7FF" }}
    >
      <header
        style={{ width: "100%", height: "96px", minHeight: "96px", display: "flex", alignItems: "center", padding: "8px 32px", backgroundColor: "#c3c3e3", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)", boxSizing: "border-box" }}
      >
        <img src={logo} alt="Abbott" style={{ height: "80px", width: "auto", objectFit: "contain" }} />
      </header>

      <Grid item xs={12} style={{ width: "100%", flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: "32px 16px", boxSizing: "border-box" }}>
        <Paper elevation={3} style={{ width: "100%", maxWidth: "596px", padding: "40px 32px", textAlign: "center", borderRadius: "8px", boxSizing: "border-box" }}>
          
          <Typography variant="h4" style={{ fontSize: "34px", fontWeight: 400, marginBottom: "20px" }}>
            Verifique su acceso
          </Typography>

          <Typography style={{ fontSize: "20px", lineHeight: 1.6 }}>
            Para continuar, ingrese el PIN enviado al correo
          </Typography>
          <Typography style={{ fontSize: "20px", lineHeight: 1.6}}>
            {censorEmail(userData.email)}, asociado al usuario {userData.username}
          </Typography>
          <Typography style={{ fontSize: "20px", lineHeight: 1.6, marginBottom: "32px"}}>
            No olvide revisar la sección de correo no deseado o spam.
          </Typography>

          <Grid container justifyContent="center" alignItems="center" spacing={1} style={{ marginBottom: error.state ? "16px" : "48px" }}>
            <Grid item>
              <Typography style={{ fontSize: "36px", marginRight: "16px" }}>PIN</Typography>
            </Grid>
            {[0, 1, 2, 3].map((index) => (
              <Grid item key={index}>
                <TextField
                  variant="outlined"
                  value={pin[index]}
                  disabled={isLoading || (index > 0 && !pin[index - 1])}
                  inputRef={(element) => { pinInputRefs.current[index] = element; }}
                  onChange={(event) => updatePinDigit(index, event)}
                  onPaste={(event) => pastePin(index, event)}
                  inputProps={{ inputMode: "numeric", style: { textAlign: "center", fontSize: "36px", color: "#4A4A4A", padding: 0, height: "60px" } }}
                  style={{ width: "75px", height: "60px", backgroundColor: "#F6F7FF", borderRadius: "8px" }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Mensaje de error estático opcional para permisos/programas */}
          {error.state && (
            <Typography color="error" style={{ marginBottom: "32px" }}>
              {error.message}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            ref={acceptButtonRef}
            disabled={!isPinComplete || isLoading}
            onClick={handleContinue}
            style={{ marginBottom: "36px" }}
          >
            {isLoading ? "Verificando..." : "Continuar"}
          </Button>

          {/* <Typography style={{ fontSize: "20px", lineHeight: 1.6, marginBottom: "12px" }}>
            ¿No tiene acceso al correo?
          </Typography>

          <Button variant="contained" color="primary" component={Link} to={routes.modifyEmailForm}>
            Solicitar cambio de correo
          </Button> */}

        </Paper>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Grid>
  );
}

export default LoginVerification;
